import {useId} from '@floating-ui/react';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import {GRAY, WHITE} from 'ui-library/Colors';

import Float, {
  ClickFloatProps,
  ControlledFloatProps,
  FloatingElementOptions,
  FloatOptions,
  FloatProps,
  HoverFloatProps,
} from './Float';
import {FloatingInteraction} from './FloatingConstants';
import {ArrowProps, FloatingPlacementOptions} from './useFloating';

const ARROW_SIZE = 8;
const POPOVER_OFFSET = 11;

function PopoverArrow({placement, arrowPos, arrowRef}: ArrowProps): JSX.Element {
  const id = useId();

  // e.g. 'top-start' -> 'top'
  const placementReduced = placement.split('-')[0];

  const arrowX = arrowPos?.x;
  const arrowY = arrowPos?.y;

  const arrowStyle: React.CSSProperties = ['top', 'bottom'].includes(placementReduced)
    ? {left: arrowX != null ? `${arrowX}px` : undefined}
    : {top: arrowY != null ? `${arrowY}px` : undefined};

  const arrowDir = {
    top: styles.arrowDown,
    right: styles.arrowLeft,
    bottom: styles.arrowUp,
    left: styles.arrowRight,
  }[placementReduced];

  return (
    <div
      id={id}
      ref={arrowRef}
      className={classNames('floating-popover-arrow', css(styles.arrow), css(arrowDir))}
      style={arrowStyle}
    />
  );
}

export type ControlledPopoverProps = Omit<ControlledFloatProps, keyof FloatOptions>;

export type HoverPopoverProps = Omit<HoverFloatProps, keyof FloatOptions>;

export type ClickPopoverProps = Omit<ClickFloatProps, keyof FloatOptions>;

export type PopoverProps = ControlledPopoverProps | HoverPopoverProps | ClickPopoverProps;

export default function Popover(props: PopoverProps): JSX.Element {
  const {children, placement, renderContent, title, isDisabled, toggleDisplayMode} = props;

  const floatingElementOptions: FloatingElementOptions = {
    floatingBodyClassName: css(styles.floatingBody),
    floatingContentClassName: css(styles.floatingContent),
    renderArrow: ({arrowRef, arrowPos, placement}) => (
      <PopoverArrow arrowRef={arrowRef} arrowPos={arrowPos} placement={placement} />
    ),
    dataTestAttrs: props.dataTestAttrs ?? {component: 'Popover', element: 'content'},
    returnFocusOnClose: props.returnFocusOnClose,
  };

  const placementOptions: FloatingPlacementOptions = {
    offsetOverride: POPOVER_OFFSET, // make room for arrow
  };

  const sharedProps: Omit<FloatProps, 'isOpen' | 'children' | 'interaction'> = {
    floatingElementOptions,
    placement,
    placementOptions,
    renderContent,
    title,
    isDisabled,
    toggleDisplayMode,
    disableContentSelection: props.disableContentSelection,
    stealFocus: props.stealFocus,
    role: props.role,
  };

  if (props.interaction === FloatingInteraction.CONTROLLED) {
    return (
      <Float
        {...sharedProps}
        interaction={props.interaction}
        isOpen={props.isOpen}
        onRequestClose={props.onRequestClose}
      >
        {children}
      </Float>
    );
  } else {
    const hoverProps =
      props.interaction === FloatingInteraction.HOVER || !props.interaction ? {delay: props.delay} : {};

    return (
      <Float {...sharedProps} {...hoverProps} interaction={props.interaction}>
        {children}
      </Float>
    );
  }
}

const BACKGROUND_COLOR = WHITE;
const BORDER_COLOR = GRAY;

const styles = StyleSheet.create({
  floatingBody: {
    background: BACKGROUND_COLOR,
    borderRadius: '4px',
    boxShadow: `0px 2px 8px 2px rgba(151, 151, 153, 0.8)`,
  },
  floatingContent: {
    padding: '6px 8px',
    maxHeight: '37.5vh',
    maxWidth: '325px',
  },
  arrow: {
    borderWidth: `${ARROW_SIZE}px`,
    position: 'absolute',
    display: 'block',
    width: 0,
    height: 0,
    borderColor: 'transparent',
    borderStyle: 'solid',
    ':after': {
      content: '""',
      borderWidth: `${ARROW_SIZE - 1}px`,
      position: 'absolute',
      display: 'block',
      width: 0,
      height: 0,
      borderColor: 'transparent',
      borderStyle: 'solid',
    },
  },
  arrowUp: {
    borderTopWidth: 0,
    top: `-${ARROW_SIZE}px`,
    borderBottomColor: `${BORDER_COLOR}`,
    ':after': {
      top: '1px',
      marginLeft: `-${ARROW_SIZE - 1}px`,
      borderTopWidth: 0,
      borderBottomColor: `${BACKGROUND_COLOR}`,
    },
  },
  arrowDown: {
    borderBottomWidth: 0,
    bottom: `-${ARROW_SIZE}px`,
    borderTopColor: `${BORDER_COLOR}`,
    ':after': {
      bottom: '1px',
      marginLeft: `-${ARROW_SIZE - 1}px`,
      borderBottomWidth: 0,
      borderTopColor: `${BACKGROUND_COLOR}`,
    },
  },
  arrowLeft: {
    borderLeftWidth: 0,
    left: `-${ARROW_SIZE}px`,
    borderRightColor: `${BORDER_COLOR}`,
    ':after': {
      left: '1px',
      marginTop: `-${ARROW_SIZE - 1}px`,
      borderLeftWidth: 0,
      borderRightColor: `${BACKGROUND_COLOR}`,
    },
  },
  arrowRight: {
    borderRightWidth: 0,
    right: `-${ARROW_SIZE}px`,
    borderLeftColor: `${BORDER_COLOR}`,
    ':after': {
      right: '1px',
      marginTop: `-${ARROW_SIZE - 1}px`,
      borderRightWidth: 0,
      borderLeftColor: `${BACKGROUND_COLOR}`,
    },
  },
});
